import { DeepResearchPoint, DeepResearchSource } from "@/pages/Private/redux/deepResearch/schema";

import { stripMarkdownInline } from "../deepResearchText.utils";

/**
 * How a section is rendered. Derived from the section key rather than from the
 * data shape, because the sections come from user-editable prompt templates —
 * anything unrecognized falls back to plain Markdown (`generic`).
 */
export type ResearchSectionKind = "summary" | "points" | "insights" | "generic";

const SECTION_KIND_BY_KEY: Record<string, ResearchSectionKind> = {
	executive_summary: "summary",
	summary: "summary",
	talking_points: "points",
	insights: "insights",
};

/**
 * Short chip labels so the whole section nav fits one row (PREC-3680: "just
 * call it Summary"). Only shortens what is genuinely long — unknown sections
 * keep their formatted header.
 */
const SHORT_LABEL_BY_KEY: Record<string, string> = {
	executive_summary: "Summary",
	talking_points: "Talking Points",
	value_proposition: "Value Prop",
	recommended_approach: "Approach",
	decision_makers: "Decision Makers",
	competitive_landscape: "Competition",
};

export const resolveSectionKind = (key: string): ResearchSectionKind =>
	SECTION_KIND_BY_KEY[key] ?? "generic";

export const resolveShortSectionLabel = (key: string, fallbackLabel: string): string =>
	SHORT_LABEL_BY_KEY[key] ?? fallbackLabel;

/** A leading "- " / "* " / "1. " bullet marker. */
const BULLET_MARKER = /^\s*(?:[-*+]|\d+[.)])\s+/;

/** `**Title:** rest` or `**Title** — rest` — the Markdown convention the prompt asks for. */
const BOLD_TITLE = /^\*\*(.+?):?\*\*[:\s—–-]*(.*)$/;

const splitTitleAndText = (line: string): DeepResearchPoint => {
	const boldMatch = line.match(BOLD_TITLE);

	if (boldMatch) {
		const title = boldMatch[1].trim();
		const text = boldMatch[2].trim();

		return { title, text: text || title };
	}

	const plain = stripMarkdownInline(line).trim();
	const separatorIndex = plain.search(/[:—–]/);

	// A short lead-in before a colon/dash reads as the point's label; anything
	// longer is a sentence, not a title.
	if (separatorIndex > 0 && separatorIndex <= 60) {
		return {
			title: plain.slice(0, separatorIndex).trim(),
			text: plain.slice(separatorIndex + 1).trim() || plain,
		};
	}

	return { title: "", text: plain };
};

/**
 * Talking points of a research result. Results produced after PREC-3680 carry a
 * structured `points` array; older ones only have the Markdown, so the bullets
 * are split back out here. That keeps every stored result — including the ones
 * that existed before this feature — rendering as numbered rows, just without
 * source pills.
 */
export const resolveTalkingPoints = (section: {
	content?: string;
	points?: DeepResearchPoint[];
}): DeepResearchPoint[] => {
	if (section.points?.length) {
		return section.points;
	}

	const lines = (section.content ?? "").split("\n");
	const bulletLines = lines.filter((line) => BULLET_MARKER.test(line));
	// Without bullets there is nothing to split on — one paragraph is one point.
	const sourceLines = bulletLines.length > 0 ? bulletLines : lines;

	return sourceLines
		.map((line) => line.replace(BULLET_MARKER, "").trim())
		.filter((line) => line.length > 0)
		.map(splitTitleAndText);
};

/** True when the result predates citations, so the panel can offer a re-run hint. */
export const hasAnySource = (
	sections: Array<{
		sources?: DeepResearchSource[];
		points?: DeepResearchPoint[];
		subsections?: Array<{ sources?: DeepResearchSource[] }>;
	}>
): boolean =>
	sections.some(
		(section) =>
			!!section.sources?.length ||
			!!section.points?.some((point) => point.sources?.length) ||
			!!section.subsections?.some((subsection) => subsection.sources?.length)
	);

/**
 * Stable identity of a pinned fact within a research result. Position-based so
 * re-pinning the same row is idempotent; a re-run that changes the content
 * simply overwrites the stored text under the same key.
 */
export const buildFactKey = (sectionKey: string, ...path: Array<number | string>): string =>
	[sectionKey, ...path].join(":");

/** Pin key for an arbitrary text selection — hashed so the same selection maps to one pin. */
export const buildSelectionFactKey = (text: string): string => {
	let hash = 0;

	for (let i = 0; i < text.length; i += 1) {
		hash = (hash << 5) - hash + text.charCodeAt(i);
		hash |= 0;
	}

	return `selection:${Math.abs(hash).toString(36)}`;
};
